<template>
    <Layout>
        <div v-if="error" class="flex flex-col items-center justify-center space-y-8 p-32">
            <p class="text-xl font-bold">{{ $t('This event is no longer available.') }}</p>
            <p>
                <InertiaLink :href="$page.props.locale == 'fr' ? `/evenements` : `/events`" class="button">
                    {{ $t('See other events') }}
                </InertiaLink>
            </p>
        </div>

        <div v-else>
            <!-- Page heading -->
            <div>
                <div class="mx-auto max-w-7xl">
                    <img class="h-48 w-full rounded-2xl object-cover lg:h-64" :src="event.banner" alt="" />
                </div>

                <div class="mx-auto -mt-16 max-w-6xl">
                    <!-- Logo & title -->
                    <div class="flex">
                        <a
                            :href="`https://${event.company.subdomain}.camaq.ca`"
                            class="z-10 hidden h-36 w-48 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl border-2 border-gray-300 bg-white md:flex"
                        >
                            <img :src="event.company.logo" alt="" class="h-32 w-32 object-contain" />
                        </a>

                        <div class="-ml-10 flex flex-1 items-center">
                            <div
                                class="flex flex-1 flex-col items-center justify-between space-y-6 rounded-lg border bg-white p-6 pl-16 shadow-lg md:flex-row md:space-y-0"
                            >
                                <div class="max-w-[300px] overflow-hidden text-ellipsis sm:max-w-[550px] md:max-w-[450px] lg:max-w-[600px]">
                                    <h1 class="text-xl font-bold text-gray-900 md:text-3xl xl:line-clamp-3" :title="event.title">
                                        {{ event.title }}
                                    </h1>
                                </div>

                                <div class="flex space-x-4">
                                    <button class="button" @click.prevent="eventInscriptionModalRef.show()">
                                        {{ $t('Confirm your presence') }}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Info cards (event infos, salary, address, etc) -->
                    <div class="mt-10 flex flex-wrap items-center justify-center gap-4">
                        <div class="badge" v-if="event.city?.length">
                            <IconMapPin :size="16" />
                            <span>{{ event.address }}, {{ event.city }}</span>
                        </div>

                        <div v-if="event.starts_at" class="badge">
                            <IconCalendar :size="16" />
                            <span>{{ $t('Starts :') }}</span>
                            <span>{{ event.starts_at }}</span>
                        </div>

                        <div v-if="event.ends_at" class="badge">
                            <IconCalendar :size="16" />
                            <span>{{ $t('Ends :') }}</span>
                            <span>{{ event.ends_at }}</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Description -->
            <div class="mx-auto mt-8 flex max-w-5xl justify-center px-8">
                <div class="prose lg:prose-lg" v-html="description"></div>
            </div>

            <div class="my-8 lg:hidden">
                <SocialSharingInline />
            </div>

            <!-- Social sharing links -->
            <SocialSharing />

            <!-- Sticky application bar -->
            <div
                class="fixed inset-x-0 bottom-0 flex flex-col items-center justify-center space-y-4 border-t bg-white p-4 shadow md:flex-row md:space-x-8 md:space-y-0"
            >
                <p class="font-medium">{{ $t('Are you interested in this event?') }}</p>

                <button class="button" @click.prevent="eventInscriptionModalRef.show()">
                    {{ $t('Confirm your presence') }}
                </button>
            </div>
        </div>

        <EventInscriptionModal ref="eventInscriptionModalRef" :event="event" />
    </Layout>
</template>

<script setup>
    import EventInscriptionModal from '@/Partials/EventInscriptionModal.vue';
    import SocialSharing from '@/Partials/SocialSharing.vue';
    import SocialSharingInline from '@/Partials/SocialSharingInline.vue';
    import { IconCalendar, IconMapPin } from '@tabler/icons-vue';
    import { onBeforeMount, ref } from 'vue';
    import { useCookieConsent } from '../../useCookieConsent';

    const { consent, replaceYoutubeIframes } = useCookieConsent();

    const props = defineProps({
        error: Boolean,
        event: Object,
    });

    const description = ref(props.event?.description);

    const eventInscriptionModalRef = ref(null);

    onBeforeMount(() => {
        if (consent != 'accept-all') {
            description.value = replaceYoutubeIframes(props.event?.description);
        }
    });
</script>
